import { isPast } from 'date-fns';
import isUndefined from 'lodash.isundefined';
import PropTypes from 'prop-types';
import React, { useContext, useEffect, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';

// components
import EventRegistrationNameFields from './EventRegistrationNameFields';
import EventRegistrationSearchByEmail from './EventRegistrationSearchByEmail';
import LoadingArea from '@molecules/LoadingArea';
import LoginPassword from '@molecules/LoginPassword';
import MemberSelection from '@molecules/MemberSelection';
import { BodyContainerStyled, BodyMedium, H3, LabelLarge } from '@styled';

// functionality
import { EventRegistrationContext } from '../EventRegistrationProvider';
import { LoadingContext } from '@context/LoadingProvider';
import { addEventRegistrationToCart } from '@general/helpers/cart.helpers';
import useCartFromAPI from '@hooks/apiLegacy/useCartFromAPI';
import useEventFromAPI from '@hooks/apiLegacy/useEventFromAPI';
import useMembersIneligibleForEventRegistration from '@hooks/useMembersIneligibleForEventRegistration';
import useViewer from '@hooks/useViewer';
import useIcscTracking from '@react/tracking/useIcscTracking';
import PasswordChangeContextProvider from '@context/PasswordChangeProvider';

const PageContainer = ({ children }) => (
    <BodyContainerStyled className="container">
        <div className="gap-8 grid-cols-8 md:grid">
            <div className="col-span-5">{children}</div>
        </div>
    </BodyContainerStyled>
);

const ErrorContainer = ({ children }) => (
    <PageContainer>
        <H3>Event Registration</H3>
        <BodyMedium className="mt-1rem">{children}</BodyMedium>
    </PageContainer>
);

PageContainer.propTypes = {
    children: PropTypes.node.isRequired,
};

ErrorContainer.propTypes = {
    children: PropTypes.node.isRequired,
};

const EventRegistrationStartPage = () => {
    // ------------------------------
    // State/Hooks
    // ------------------------------

    const { registrationEmail, searchResultCountForEmail } = useContext(EventRegistrationContext);
    const { setIsLoading } = useContext(LoadingContext);
    const [fullProgram, setFullProgram] = useState();
    const history = useHistory();
    const { code } = useParams();
    const { Track } = useIcscTracking({
        app_area: 'Event Registration Start',
    });

    const { viewer } = useViewer();
    const { cart } = useCartFromAPI({ viewerId: viewer?.recordId });
    const { event } = useEventFromAPI({ code });
    const { membersIneligibleForEventRegistration } = useMembersIneligibleForEventRegistration({ eventCode: code });
    const { search, pathname: returnUrl } = useLocation();

    // ------------------------------
    // Effects
    // ------------------------------

    useEffect(() => {
        if (!event) {
            return;
        }

        setFullProgram(event.functions.find(f => f.code === 'FP') || null);
    }, [event]);

    // ------------------------------
    // Functions
    // ------------------------------

    const redirectAfterMemberSelection = async ({ whoFor, selectedCoworkers }) => {
        // fire pixel code for 2024LASVEGAS when continuing to registration
        if (event.code === '2024LASVEGAS') {
            // Trigger pixel code
            const pixelSrc = 'https://sp.analytics.yahoo.com/spp.pl?a=10000&.yp=10150340&he={INSERT_MACRO_HERE}&hph={INSERT_MACRO_HERE}&ec=lv24contregbutton';

            // Create a new image element
            const img = new Image();

            // Set the src attribute to the pixel URL
            img.src = pixelSrc;

            // Append the image to the document body to trigger pixel request
            document.body.appendChild(img);
        }

        // fire pixel code for 2024NY when continuing to registration
        if (event.code === '2024NY') {
            // Trigger pixel code
            const pixelSrc = 'https://sp.analytics.yahoo.com/spp.pl?a=10000&.yp=10150340&he={INSERT_MACRO_HERE}&hph={INSERT_MACRO_HERE}&ec=contemail2';

            // Create a new image element
            const img = new Image();

            // Set the src attribute to the pixel URL
            img.src = pixelSrc;

            // Append the image to the document body to trigger pixel request
            document.body.appendChild(img);
        }


        if (whoFor === 'me') {
            history.push(`/event-registration/${code}/info`);
        } else if (whoFor === 'coworker') {
            setIsLoading(true);
            for (const coworker of selectedCoworkers) {
                await addEventRegistrationToCart({
                    cart,
                    event,
                    memberId: coworker.value,
                    viewerId: viewer.recordId,
                    useSpeedBump: true,
                });
            }

            history.push(`/cart/view`);
            setIsLoading(false);
        } else if (whoFor === 'new') {
            history.push(`/event-registration/${code}/info/new`);
        }
    };

    // ------------------------------
    // Render
    // ------------------------------

    if (isUndefined(viewer) || isUndefined(event) || (event && !fullProgram)) {
        return <LoadingArea />;
    }

    if (viewer && !membersIneligibleForEventRegistration) {
        return <LoadingArea />;
    }

    if (event === null) {
        return (
            <ErrorContainer>
                Event not found. Please visit our{' '}
                <a className="text-link" href="/search?type=events">
                    events page
                </a>{' '}
                to view upcoming events.
            </ErrorContainer>
        );
    }

    if (isPast(new Date(event.online_registration_cutoff))) {
        return (
            <ErrorContainer>
                Registration for {event.title} has passed. Please visit our{' '}
                <a className="text-link" href="/search?type=events">
                    events page
                </a>{' '}
                to view upcoming events.
            </ErrorContainer>
        );
    }

    if (fullProgram.fees.length === 0) {
        return (
            <ErrorContainer>
                An error occurred while loading your event. Please contact{' '}
                <a className="text-link">membership@icsc.com</a> if you continue to experience issues.
            </ErrorContainer>
        );
    }

    if (event.maximum_attendance && event.seats_remaining === 0) {
        return (
            <ErrorContainer>
                {event.title} is sold out. Please visit our{' '}
                <a className="text-link" href="/search?type=events">
                    events page
                </a>{' '}
                to view other upcoming events.
            </ErrorContainer>
        );
    }

    const redirectOnSuccessfulSearch = (email, count) => {
        if (count > 0) {
            const query = new URLSearchParams(search);
            query.append('returnUrl', returnUrl);
            history.push({
                pathname: `/login`,
                search: `${query}`,
                state: { email },
            });
        }
    };

    return (
        <Track>
            <PageContainer>
                {!viewer && (
                    <>
                        <EventRegistrationSearchByEmail
                            hideButton={!!registrationEmail}
                            eventCode={event.code}
                            onSuccessfulSearch={redirectOnSuccessfulSearch}
                        />

                        {registrationEmail && searchResultCountForEmail === 0 && (
                            <div className="mt-2rem">
                                <PasswordChangeContextProvider>
                                    <EventRegistrationNameFields />
                                </PasswordChangeContextProvider>
                            </div>
                        )}
                    </>
                )}

                {viewer && (
                    <div>
                        <H3 className="mb-1.125rem">Event Registration</H3>
                        <MemberSelection
                            buttonText="Continue to Registration"
                            headingText="Who is this event registration for?"
                            membersToExclude={membersIneligibleForEventRegistration}
                            onSubmit={redirectAfterMemberSelection}
                        />
                    </div>
                )}
            </PageContainer>
        </Track>
    );
};

export default EventRegistrationStartPage;
